// frontend/src/pages/About.jsx
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faUsers,
  faTractor,
  faLeaf,
  faHandshake,
  faCheckCircle,
  faTruck,
  faGlobeAfrica,
  faStar,
  faQuoteLeft,
  faAward,
  faRocket,
  faHeart,
  faBullseye,
  faBuilding,
  faArrowRight,
  faSpinner,
  faUserCircle,
  faEnvelope,
  faPhone,
  faExclamationTriangle,
  faGlobe,
  faShip,
  faWarehouse,
  faClock,
  faCalendarCheck,
  faShieldAlt,
  faBriefcase,
  faUserTie,
  faUsers as faUsersIcon,
  faCode,
  faLightbulb,
  faClipboardList,
  faUserPlus,
  faUserCog,
  faHandshake as faHandshakeIcon,
  faInfoCircle,
  faArrowUpRightFromSquare,
  faWhatsapp,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import { getPublicTeamMembers } from "../api";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO/SEO";
import "../styles/about.scss";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// TEAM ROLE MAPPING
// ============================================
const TEAM_ROLE_LABELS = {
  sale_representative: "Sales Representative",
  advisor: "Advisor",
  coordinator: "Coordinator",
  assistant_advisor: "Assistant Advisor",
  assistant_coordinator: "Assistant Coordinator",
  manager: "Manager",
  assistant_manager: "Assistant Manager",
  administrator: "Administrator",
  community_relation: "Community & Farmer Relation",
  developer: "Developer",
};

const TEAM_ROLE_ICONS = {
  sale_representative: faHandshakeIcon,
  advisor: faLightbulb,
  coordinator: faClipboardList,
  assistant_advisor: faUserPlus,
  assistant_coordinator: faUserCog,
  manager: faBriefcase,
  assistant_manager: faUserTie,
  administrator: faShieldAlt,
  community_relation: faUsersIcon,
  developer: faCode,
};

const DEFAULT_TEAM_ROLE = faUserCircle;

// ============================================
// DATA
// ============================================
const VALUES = [
  {
    icon: faSeedling,
    title: "Quality First",
    description:
      "We source only the highest quality ingredients from trusted global manufacturers with full traceability.",
  },
  {
    icon: faHandshake,
    title: "Farmer-Centric",
    description:
      "Every product is designed with the farmer's success and profitability in mind.",
  },
  {
    icon: faLeaf,
    title: "Sustainable Nutrition",
    description:
      "Committed to sustainable livestock nutrition solutions for African agriculture.",
  },
  {
    icon: faGlobeAfrica,
    title: "African Growth",
    description:
      "Dedicated to strengthening agricultural value chains across Sierra Leone and West Africa.",
  },
];

const MILESTONES = [
  {
    year: "2024",
    title: "Company Founded",
    description:
      "Kenali Enterprise established to serve Sierra Leone's livestock sector.",
  },
  {
    year: "2024",
    title: "First Shipment",
    description: "Initial product shipment from trusted global partners.",
  },
  {
    year: "2025",
    title: "Product Launch",
    description: "Official launch of GrowPro, DairyBoost, and Mineralite.",
  },
  {
    year: "2025",
    title: "Partnerships",
    description:
      "Strategic partnerships with leading global manufacturers established.",
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const About = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================
  // FETCH TEAM DATA
  // ============================================
  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getPublicTeamMembers();
      const data = response.data;

      if (data.success) {
        setTeamMembers(data.team || []);
        setTeamStats(data.stats || null);
      } else {
        setTeamMembers([]);
        setError(data.error || "Failed to load team members");
      }
    } catch (err) {
      console.error("Error fetching team data:", err);
      setError("Could not load team members. Please try again later.");
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleIcon = (teamRole) => {
    return TEAM_ROLE_ICONS[teamRole] || DEFAULT_TEAM_ROLE;
  };

  const getRoleLabel = (teamRole) => {
    return TEAM_ROLE_LABELS[teamRole] || teamRole || "Team Member";
  };

  // ============================================
  // HANDLE TEAM MEMBER CLICK
  // ============================================
  const handleTeamMemberClick = (member) => {
    if (user) {
      if (user._id === member.id) {
        navigate("/profile");
        return;
      }
      if (isAdmin() || isSuperAdmin()) {
        navigate(`/profile/${member.id}`);
        return;
      }
      navigate(`/profile/${member.id}`);
    } else {
      navigate(`/profile/${member.id}`);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <SEO
        title="About Kenali Enterprise - Premium Livestock Feed Solutions"
        description="Kenali Enterprise is a trusted provider of premium livestock feed supplements and animal nutrition products in Sierra Leone. Learn about our mission and values."
        keywords="about Kenali Enterprise, livestock feed company Sierra Leone, animal nutrition, farming solutions"
        canonical="https://kenalienterprise.com/about"
      />
      <motion.div
        className="about-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="about-hero">
          <div className="about-hero__overlay" />
          <div className="container about-hero__content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faSeedling} className="about-hero__icon" />
              <h1>About Kenali Enterprise</h1>
              <p>
                Premium livestock nutrition solutions for healthier animals and
                improved farm productivity. Sourced from trusted global
                partners.
              </p>
              <div className="about-hero__badges">
                <span>
                  <FontAwesomeIcon icon={faGlobe} /> Global Sourcing
                </span>
                <span>
                  <FontAwesomeIcon icon={faBuilding} /> Quality Partners
                </span>
                <span>
                  <FontAwesomeIcon icon={faShip} /> International Supply Chain
                </span>
                {teamStats && teamStats.total > 0 && (
                  <span>
                    <FontAwesomeIcon icon={faUsers} /> {teamStats.total} Team
                    Members
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Story */}
        <section className="container about-story">
          <div className="about-story__grid">
            <motion.div
              className="about-story__text"
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="section-tag">
                <FontAwesomeIcon icon={faInfoCircle} /> Our Story
              </span>
              <h2>Building Africa's Agricultural Future</h2>
              <div className="gold-divider" />
              <p>
                Kenali Enterprise was founded with a mission to bring premium
                livestock nutrition solutions to farmers across Sierra Leone. We
                understand the challenges farmers face in accessing quality feed
                supplements that genuinely improve animal performance and farm
                profitability.
              </p>
              <p>
                Our name, <strong>Kenali</strong>, represents the combination of
                our founding team's vision and commitment to agricultural
                transformation in Africa. We believe in the power of quality
                nutrition to transform livestock production and farmer
                livelihoods.
              </p>
              <p>
                We source our products from trusted global manufacturers,
                ensuring every product meets rigorous quality standards before
                reaching our farmers.
              </p>
              <div className="about-story__actions">
                <Link to="/products" className="btn btn-primary">
                  Explore Our Products <FontAwesomeIcon icon={faArrowRight} />
                </Link>
                <Link to="/contact" className="btn btn-outline">
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.div
              className="about-story__mission"
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FontAwesomeIcon icon={faTractor} className="mission-icon" />
              <h3>Our Mission</h3>
              <p>
                To empower African farmers with premium livestock nutrition
                solutions that enhance animal health, productivity, and
                profitability.
              </p>
              <div className="mission-stats">
                <div>
                  <div className="stat-number">500+</div>
                  <div className="stat-label">Happy Farmers</div>
                </div>
                <div>
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Tons Delivered</div>
                </div>
                <div>
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Quality Guarantee</div>
                </div>
              </div>
              <div className="mission-supplier">
                <FontAwesomeIcon icon={faShip} />
                <span>Proudly sourced from trusted global partners</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="about-values">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">
                <FontAwesomeIcon icon={faHeart} /> Our Values
              </span>
              <h2>What Drives Kenali Enterprise</h2>
              <p className="section-subtitle">
                The principles that guide our work and relationships
              </p>
            </div>
            <div className="values-grid">
              {VALUES.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-card"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                >
                  <div className="value-icon-wrapper">
                    <FontAwesomeIcon icon={value.icon} />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="about-milestones">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">
                <FontAwesomeIcon icon={faClock} /> Our Journey
              </span>
              <h2>Company Milestones</h2>
              <p className="section-subtitle">
                Key moments in Kenali Enterprise's journey
              </p>
            </div>
            <div className="milestones-grid">
              {MILESTONES.map((milestone, index) => (
                <motion.div
                  key={index}
                  className="milestone-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="milestone-year">{milestone.year}</div>
                  <h4>{milestone.title}</h4>
                  <p>{milestone.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="about-team">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">
                <FontAwesomeIcon icon={faUsers} /> Our Team
              </span>
              <h2>Meet the Team</h2>
              <p className="section-subtitle">
                The dedicated people behind Kenali Enterprise
              </p>

              {teamStats && !loading && (
                <div className="team-stats-summary">
                  <div className="team-stat-item">
                    <span className="team-stat-value">
                      {teamStats.total || 0}
                    </span>
                    <span className="team-stat-label">Total Members</span>
                  </div>
                  <div className="team-stat-item">
                    <span className="team-stat-value">
                      {teamStats.approved || 0}
                    </span>
                    <span className="team-stat-label">Active</span>
                  </div>
                  {teamStats.byRole && (
                    <div className="team-stat-item">
                      <span className="team-stat-value">
                        {Object.keys(teamStats.byRole).length}
                      </span>
                      <span className="team-stat-label">Roles</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {loading ? (
              <div className="team-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading team members...</span>
              </div>
            ) : error ? (
              <div className="team-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="team-empty">
                <FontAwesomeIcon icon={faUsers} />
                <h3>No Team Members Found</h3>
                <p>Check back later to meet our team.</p>
              </div>
            ) : (
              <>
                <div className="team-grid">
                  <AnimatePresence>
                    {teamMembers.map((member, index) => {
                      const roleIcon = getRoleIcon(member.teamRole);
                      const roleLabel = getRoleLabel(member.teamRole);
                      const isApproved = member.isApproved !== false;

                      return (
                        <motion.div
                          key={member.id || index}
                          className={`team-card ${!isApproved ? "pending" : ""}`}
                          initial={{ y: 20, opacity: 0 }}
                          whileInView={{ y: 0, opacity: 1 }}
                          transition={{ delay: Math.min(index * 0.08, 0.5) }}
                          viewport={{ once: true }}
                          whileHover={{ y: -4 }}
                          layout
                          onClick={() => handleTeamMemberClick(member)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="team-avatar">
                            {member.avatar || member.profileImage ? (
                              <img
                                src={member.avatar || member.profileImage}
                                alt={member.name}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.textContent =
                                    getInitials(member.name);
                                }}
                              />
                            ) : (
                              getInitials(member.name)
                            )}
                          </div>

                          <h3>
                            {member.name}
                            {!isApproved && (
                              <span className="pending-badge">Pending</span>
                            )}
                          </h3>

                          <div className="team-role">
                            <FontAwesomeIcon
                              icon={roleIcon}
                              className="role-icon"
                            />
                            {roleLabel}
                          </div>

                          <p>{member.bio}</p>

                          <div className="team-contact-links">
                            {member.email && (
                              <a
                                href={`mailto:${member.email}`}
                                className="team-contact"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faEnvelope} />
                                <span>{member.email}</span>
                              </a>
                            )}
                            {member.phone && (
                              <a
                                href={`tel:${member.phone}`}
                                className="team-contact"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faPhone} />
                                <span>{member.phone}</span>
                              </a>
                            )}
                            {member.phone && (
                              <a
                                href={`https://wa.me/${member.phone.replace(/[^0-9]/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="team-contact whatsapp-contact"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faWhatsappBrand} />
                                <span>WhatsApp</span>
                              </a>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>

                {teamStats && !loading && teamStats.byRole && (
                  <div className="team-roles-footer">
                    <h4>
                      <FontAwesomeIcon icon={faBriefcase} /> Team Roles
                    </h4>
                    <div className="team-roles-grid">
                      {Object.entries(teamStats.byRole).map(([role, count]) => (
                        <div key={role} className="team-role-chip">
                          <FontAwesomeIcon icon={getRoleIcon(role)} />
                          <span>{getRoleLabel(role)}</span>
                          <span className="role-count">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-cta">
          <div className="container">
            <div className="cta-content">
              <div className="cta-badge">
                <FontAwesomeIcon icon={faHandshake} /> Partner With Us
              </div>
              <h2>Ready to Partner with Kenali Enterprise?</h2>
              <p>
                Let's work together to improve livestock productivity in Sierra
                Leone and across Africa.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  <FontAwesomeIcon icon={faArrowRight} /> Contact Us
                </Link>
                <Link to="/products" className="btn btn-outline-white">
                  Browse Products
                </Link>
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-cta"
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default About;
